import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { apiClient } from '../services/apiClient';
import { articleService } from '../services/articleService';
import './UploadArticle.css';
import { useNavigate } from 'react-router-dom';
import { getBasename } from '../utils/pathUtils';

interface DuplicateArticle {
  id: number;
  title: string;
  doi: string;
  pdfUrl: string;
  journal: string | null;
}

interface ProcessedData {
  title: string;
  doi: string;
  journal: string;
  keywords: string[];
  fileUuid: string;
  pdfPath: string;
  coverUrl: string | null;
  publishedDate: string | null;
  hasThumbnail?: boolean;
  thumbnailPath?: string;
}

export default function UploadArticle() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [duplicateArticle, setDuplicateArticle] = useState<DuplicateArticle | null>(null);

  const [step, setStep] = useState<'upload' | 'confirm'>('upload');
  const [processedData, setProcessedData] = useState<ProcessedData | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [editedKeywords, setEditedKeywords] = useState<string[]>([]);

  const navigate = useNavigate();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'application/pdf') {
      await handlePdfUpload(file);
    } else {
      setUploadError('Por favor, envie apenas arquivos PDF');
    }
  }, []);

  const onThumbnailDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      if (!processedData?.fileUuid) {
        setUploadError('UUID do arquivo não encontrado');
        return;
      }

      const file = acceptedFiles[0];
      if (!file) return;

      setIsUploading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('thumbnail', file);

      const response = await apiClient.uploadThumbnail(processedData.fileUuid, formData);

      if (response.success && response.thumbnailUrl) {
        // Adicionar timestamp para evitar cache
        const thumbnailUrlWithTimestamp = `${response.thumbnailUrl}?t=${Date.now()}`;
        
        console.log('✅ Thumbnail atualizada:', {
          thumbnailUrl: response.thumbnailUrl,
          thumbnailPath: response.thumbnailPath,
          urlWithTimestamp: thumbnailUrlWithTimestamp
        });

        // Atualizar estados
        setThumbnailUrl(thumbnailUrlWithTimestamp);
        setProcessedData(prev => prev ? {
          ...prev,
          coverUrl: thumbnailUrlWithTimestamp,
          hasThumbnail: true,
          thumbnailPath: response.thumbnailPath
        } : null);
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('❌ Erro ao fazer upload da thumbnail:', error);
      setUploadError(error instanceof Error ? error.message : 'Erro ao fazer upload da thumbnail');
    } finally {
      setIsUploading(false);
    }
  }, [processedData?.fileUuid]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  const { getRootProps: getThumbnailRootProps, getInputProps: getThumbnailInputProps } = useDropzone({
    onDrop: onThumbnailDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    maxFiles: 1
  });

  const handlePdfUpload = async (file: File, forceSave: boolean = false) => {
    try {
      setIsUploading(true);
      setUploadError(null);

      if (!forceSave) {
        // Primeiro verificar se é duplicado
        const duplicateCheck = await apiClient.checkPDFDuplicate(file);
        
        if (duplicateCheck.isDuplicate) {
          setDuplicateArticle(duplicateCheck.existingArticle);
          setShowDuplicateModal(true);
          return;
        }
      }

      // Se não for duplicado ou forceSave=true, continuar com o processamento completo
      console.log('✅ Iniciando processamento completo do PDF...');
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('forceSave', forceSave.toString());

      const response = await apiClient.uploadPdf(formData);

      if (response.article) {
        // Garantir que a URL da thumbnail está correta
        const coverUrl = response.article.coverUrl 
          ? `${import.meta.env.VITE_API_URL}/thumbnails/${getBasename(response.article.coverUrl)}`
          : null;

        setProcessedData({
          title: response.article.title,
          doi: response.article.doi || '',
          journal: response.article.journal || '',
          keywords: response.article.keywords || [],
          fileUuid: response.article.fileUuid,
          pdfPath: response.article.pdfUrl,
          coverUrl: coverUrl,
          publishedDate: response.article.publishedDate,
          hasThumbnail: !!coverUrl,
          thumbnailPath: response.article.thumbnailPath
        });
        setStep('confirm');
      }

    } catch (error) {
      console.error('❌ Erro ao processar PDF:', error);
      setUploadError(error instanceof Error ? error.message : 'Erro ao processar o PDF');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDuplicateAction = async (action: 'view' | 'upload' | 'cancel') => {
    if (action === 'view' && duplicateArticle) {
      window.open(duplicateArticle.pdfUrl, '_blank');
    } else if (action === 'upload') {
      // Reenviar o arquivo com forceSave = true
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput?.files?.[0]) {
        console.log('🔄 Reenviando arquivo com forceSave = true');
        await handlePdfUpload(fileInput.files[0], true);
      }
      setShowDuplicateModal(false);
    } else {
      await handleCancel();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsUploading(true);
      
      if (!processedData || !processedData.fileUuid) {
        throw new Error('Dados do artigo não encontrados');
      }

      const response = await articleService.saveProcessedArticle(
        processedData.fileUuid,
        {
          title: processedData.title,
          doi: processedData.doi,
          journal: processedData.journal,
          keywords: editedKeywords,
          publishedDate: processedData.publishedDate
        }
      );

      if (response.success) {
        // Usar o origin atual do navegador (hostname + protocolo)
        const currentOrigin = window.location.origin;
        const articleUrl = `${currentOrigin}/article/${response.article.id}`;
        
        console.log('✅ Artigo salvo, redirecionando:', {
          articleId: response.article.id,
          url: articleUrl
        });

        // Redirecionar usando a URL completa
        window.location.href = articleUrl;
      }
    } catch (error) {
      console.error('Erro ao salvar artigo:', error);
      setUploadError(error instanceof Error ? error.message : 'Erro ao salvar artigo');
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (processedData?.keywords) {
      setEditedKeywords(processedData.keywords);
    }
  }, [processedData?.keywords]);

  useEffect(() => {
    console.log('ProcessedData atualizado:', processedData);
  }, [processedData]);

  const renderThumbnail = () => {
    // Usar diretamente a URL mais recente
    const thumbnailSrc = processedData?.coverUrl;
    
    console.log('🖼️ Renderizando thumbnail:', {
      processedDataCoverUrl: processedData?.coverUrl,
      using: thumbnailSrc
    });
    
    return (
      <div className="thumbnail-container">
        <div 
          {...getThumbnailRootProps()} 
          className={`thumbnail-dropzone ${thumbnailSrc ? 'has-thumbnail' : ''}`}
        >
          <input {...getThumbnailInputProps()} />
          {thumbnailSrc ? (
            <>
              <img 
                key={`${thumbnailSrc}?t=${Date.now()}`} // Forçar re-render com timestamp
                src={thumbnailSrc} 
                alt="Capa do artigo" 
                className="thumbnail-preview"
                onError={(e) => {
                  console.error('❌ Erro ao carregar imagem:', e);
                  e.currentTarget.src = '/placeholder-thumbnail.jpg';
                }}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
              <div className="thumbnail-overlay">
                <span>Clique ou arraste para trocar a imagem</span>
              </div>
            </>
          ) : (
            <div className="thumbnail-placeholder">
              <span>Clique ou arraste uma imagem para a capa</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDuplicateModal = () => {
    console.log('Renderizando modal de duplicação:', {
      showDuplicateModal,
      duplicateArticle
    });

    if (!showDuplicateModal || !duplicateArticle) return null;

    return (
      <div className="modal-overlay">
        <div className="duplicate-modal">
          <button 
            onClick={() => handleDuplicateAction('cancel')} 
            className="close-button"
            aria-label="Fechar modal"
          >
            ×
          </button>

          <div className="modal-header">
            <h3>Artigo Duplicado Encontrado!</h3>
          </div>

          <div className="modal-content">
            <div className="info-row">
              <span className="info-label">Título:</span>
              <span className="info-value">{duplicateArticle.title}</span>
            </div>
            <div className="info-row">
              <span className="info-label">DOI:</span>
              <span className="info-value">{duplicateArticle.doi}</span>
            </div>
            {duplicateArticle.journal && (
              <div className="info-row">
                <span className="info-label">Revista:</span>
                <span className="info-value">{duplicateArticle.journal}</span>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button onClick={() => handleDuplicateAction('view')} className="btn primary">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
              </svg>
              Mostrar o Artigo
            </button>
            <button onClick={() => handleDuplicateAction('upload')} className="btn danger">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"/>
              </svg>
              Fazer Upload Mesmo Assim
            </button>
            <button onClick={() => handleDuplicateAction('cancel')} className="btn secondary">
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleCancel = async () => {
    try {
      if (processedData?.fileUuid) {
        // Limpar apenas arquivos temporários
        await apiClient.cancelProcessing(processedData.fileUuid);
      }
    } catch (error) {
      console.error('❌ Erro ao cancelar processamento:', error);
    } finally {
      // Limpar estados
      setStep('upload');
      setProcessedData(null);
      setDuplicateArticle(null);
      setThumbnailUrl(null);
      setEditedKeywords([]);
      setNewKeyword('');
      
      // Redirecionar para a página principal do projeto
      navigate('/');
    }
  };

  // Limpar URLs temporárias ao desmontar ou cancelar
  useEffect(() => {
    return () => {
      if (thumbnailUrl && thumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [thumbnailUrl]);

  return (
    <div className="upload-article-container">
      {uploadError && <div className="error-message">{uploadError}</div>}
      {renderDuplicateModal()}
      
      {step === 'upload' ? (
        <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          {isUploading ? (
            <p>Processando arquivo...</p>
          ) : (
            <p>Arraste um arquivo PDF ou clique para selecionar</p>
          )}
        </div>
      ) : (
        <div className="confirmation-form">
          <h2>Confirme os dados do artigo</h2>
          
          <div className="form-content">
            <div className="form-left-column">
              {renderThumbnail()}
            </div>
            
            <div className="form-right-column">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Título:</label>
                  <input
                    type="text"
                    value={processedData?.title || ''}
                    onChange={(e) => setProcessedData(prev => prev ? {...prev, title: e.target.value} : null)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>DOI:</label>
                  <input
                    type="text"
                    value={processedData?.doi || ''}
                    onChange={(e) => setProcessedData(prev => prev ? {...prev, doi: e.target.value} : null)}
                  />
                </div>
                
                <div className="form-group">
                  <label>Journal:</label>
                  <input
                    type="text"
                    value={processedData?.journal || ''}
                    onChange={(e) => setProcessedData(prev => prev ? {...prev, journal: e.target.value} : null)}
                  />
                </div>

                <div className="form-group">
                  <label>Data de Publicação:</label>
                  <input
                    type="text"
                    value={processedData?.publishedDate || ''}
                    onChange={(e) => setProcessedData(prev => prev ? {...prev, publishedDate: e.target.value} : null)}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
                
                <div className="form-group">
                  <label>Palavras-chave:</label>
                  <div id="upload-keywords-editor" className="keywords-editor">
                    <div className="keywords-container">
                      {editedKeywords.map((keyword, index) => (
                        <div key={index} className="keyword-tag">
                          <span>{keyword}</span>
                          <button 
                            onClick={(e) => {
                              e.preventDefault(); // Prevenir comportamento padrão
                              const newKeywords = editedKeywords.filter((_, i) => i !== index);
                              setEditedKeywords(newKeywords);
                            }} 
                            type="button" // Explicitamente definir como button para não submeter o form
                            className="remove-keyword"
                            aria-label="Remover palavra-chave"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="keyword-input-container">
                      <input
                        type="text"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newKeyword.trim()) {
                            e.preventDefault();
                            setEditedKeywords(prev => [...prev, newKeyword.trim()]);
                            setNewKeyword('');
                          }
                        }}
                        placeholder="Digite uma palavra-chave e pressione Enter"
                        className="keyword-input"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={handleCancel} 
                    className="btn-secondary"
                    disabled={isUploading}
                  >
                    {isUploading ? 'Aguarde...' : 'Voltar'}
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={isUploading}
                  >
                    {isUploading ? 'Salvando...' : 'Salvar Artigo'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 